import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MergeDownloadFile } from '../types';
import { track } from './analytics';
import { useMergeDownloadLink } from './useApiMutation';
import { triggerDownload } from './useFileDownload';

export const DEFAULT_MERGED_FILE_NAME = 'Merged Documents.pdf'

/**
 * Requests a merged PDF for the given files and downloads it once the backend
 * returns a link. Files are merged in the order they are passed.
 */
export const useMergedFileDownload = ({ CompanyIdOrderIdHash, onDownloaded }: {
  CompanyIdOrderIdHash: string;
  /** Called after the merged file download has been triggered. */
  onDownloaded?: () => void;
}) => {
  const { search } = useLocation()
  const { data, mutate, error, isLoading, reset } = useMergeDownloadLink({ CompanyIdOrderIdHash, search })
  const [isDownloading, setIsDownloading] = useState(false)
  const fileCountRef = useRef(0)
  const onDownloadedRef = useRef(onDownloaded)
  onDownloadedRef.current = onDownloaded

  useEffect(() => {
    if (error) {
      track('error', 'useMergeDownloadLink', {}, {
        message: JSON.stringify(error),
        component: 'useMergedFileDownload',
        fileCount: fileCountRef.current,
      })
      console.log('[api] useMergeDownloadLink ', error)
    }
  }, [error])

  useEffect(() => {
    if (data && data.Url) {
      const fileName = data.FileName || DEFAULT_MERGED_FILE_NAME
      setIsDownloading(true)
      track('Download merged initialized', fileName, {}, { fileCount: fileCountRef.current })

      triggerDownload(data.Url, fileName).then(() => {
        setTimeout(() => {
          setIsDownloading(false)
        }, 500);

        // NOTE: Like useFileDownload, reaching this point doesn't guarantee the
        // browser finished saving the file.
        track('Download merged complete', fileName, {}, { fileCount: fileCountRef.current })
        onDownloadedRef.current?.()
      })
    }
  }, [data])

  const merge = (files: MergeDownloadFile[]) => {
    fileCountRef.current = files.length
    track('Merge documents requested', '', {}, { fileCount: files.length })
    mutate({ Files: files })
  }

  return {
    merge,
    isMerging: isLoading || isDownloading,
    error,
    reset,
  }
}
